import { z } from "zod";

import { CAPTAIN_BALANCE_EVENT_TYPES, EVENT_TYPES, REMOTE_FAILURE_EVENT_TYPES } from "./event-types";

import type { ProblemDetails } from "@/lib/helpers/handleProblemDetailsError";

export const notificationSeveritySchema = z.enum(["Info", "Warning", "Error"]);

export const notificationStatusSchema = z.enum(["unread", "inbox", "archived"]);

const campaignPayloadBaseSchema = z.object({
  campaignId: z.string().nullish(),
  campaignName: z.string(),
  integrationName: z.string(),
  clientModuleIntegrationId: z.string(),
});

const campaignUpsertedChangeSchema = campaignPayloadBaseSchema.extend({
  month: z.string(),
  previousAmount: z.coerce.number().nullish(),
  amount: z.coerce.number().nullish(),
});

const remoteCampaignFailureChangeSchema = campaignPayloadBaseSchema.extend({
  errorMessage: z.string(),
  errorCode: z.string().nullish(),
});

const companyRegisteredChangeSchema = z.object({
  companyId: z.string(),
  integrationName: z.string(),
  contactEmail: z.string(),
});

const captainBalanceAlertChangeSchema = z.object({
  integrationName: z.string(),
  month: z.string(),
  availableTokens: z.coerce.number(),
  monthlyCapTokens: z.coerce.number(),
  topUpBalanceTokens: z.coerce.number(),
  thresholdPercent: z.coerce.number(),
});

const notificationBaseSchema = z.object({
  id: z.string(),
  clientName: z.string().nullish(),
  clientId: z.string().nullish(),
  actorName: z.string().nullable(),
  severity: notificationSeveritySchema,
  createdAt: z.string(),
  readAt: z.string().nullable().default(null),
  archivedAt: z.string().nullable().default(null),
});

function notificationOf<TEventType extends z.ZodType<string>, TChange extends z.ZodType>(
  eventType: TEventType,
  change: TChange,
) {
  return notificationBaseSchema.extend({
    eventType,
    change: change.nullable().catch((ctx) => {
      console.error("[notifications] invalid change payload:", ctx.error);
      return null;
    }),
  });
}

const knownNotificationSchema = z.discriminatedUnion("eventType", [
  notificationOf(z.literal(EVENT_TYPES.campaignsUpserted), campaignUpsertedChangeSchema),
  notificationOf(z.enum(REMOTE_FAILURE_EVENT_TYPES), remoteCampaignFailureChangeSchema),
  notificationOf(z.literal(EVENT_TYPES.integrationsCompanyRegistered), companyRegisteredChangeSchema),
  notificationOf(z.enum(CAPTAIN_BALANCE_EVENT_TYPES), captainBalanceAlertChangeSchema),
]);

const unmappedNotificationSchema = notificationBaseSchema.extend({
  eventType: z.string(),
  change: z.null().catch(null),
});

export const notificationSchema = z.union([knownNotificationSchema, unmappedNotificationSchema]);

export const notificationProblemSchema = z.object({ title: z.string() }) satisfies z.ZodType<
  Pick<ProblemDetails, "title">
>;

export const notificationsResponseSchema = z.object({
  data: z.array(notificationSchema),
  page: z.number(),
  pageSize: z.number(),
  totalRecords: z.number(),
});

const filterOptionSchema = z.object({
  label: z.string(),
  value: z.string(),
  count: z.number(),
});

export const notificationFilterOptionsSchema = z.object({
  severity: z.array(filterOptionSchema.extend({ value: notificationSeveritySchema })),
  actor: z.array(filterOptionSchema),
  client: z.array(filterOptionSchema),
});

export const notificationFiltersSchema = z.object({
  status: notificationStatusSchema,
  search: z.string().optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  severities: z.array(notificationSeveritySchema),
  actorIds: z.array(z.string()),
  clientIds: z.array(z.string()),
});

export const notificationSettingSchema = z.object({
  type: z.string(),
  enabled: z.boolean(),
});

export const notificationSettingsResponseSchema = z.object({
  settings: z.array(notificationSettingSchema),
});
